var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var Checkbox_1;
import React, { Component, createRef } from 'react';
import { Alignment, Placement } from '@philips-internal/oswald-ui-constants';
import { AwesomeComponent, ComponentType } from '@philips-internal/oswald-ui-core';
import { bool, eventListener, expression, formatters, number, oneOfType, string } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { ChangeEvent, FocusEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { EditorService, MessageService, ModelService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { classNames, defaultClasses, isContentfulString, isFunction, shallowCompare } from '@philips-internal/oswald-ui-utils';
import { ReactComponent as CheckMark } from '../../../resources/assets/components/DLS_Checkmark.svg';
import { ReactComponent as InformationCircleIcon } from '../../../resources/assets/components/DLS_InformationCircle.svg';
import { Tooltip } from '../tooltip/tooltip.js';
import { CheckboxAPI } from './checkbox-api.js';
import './checkbox.css';
let Checkbox = Checkbox_1 = class Checkbox extends Component {
    constructor() {
        super(...arguments);
        this.checkboxRef = createRef();
        this.onChange = (event) => {
            const { onChange, value: oldValue } = this.props;
            const value = event.currentTarget.checked;
            if (isFunction(onChange)) {
                onChange(new ChangeEvent({
                    oldValue,
                    prettyValue: Checkbox_1.formatPrettyValue({ ...this.props, value }),
                    value,
                }));
            }
        };
        this.onFocus = () => {
            const { onFocus } = this.props;
            onFocus && onFocus(new FocusEvent());
        };
        this.onBlur = () => {
            const { onBlur } = this.props;
            onBlur && onBlur(new FocusEvent());
        };
    }
    static getLabelText(props) {
        const { formatters } = props;
        const { label = Checkbox_1.defaultProps.label } = ModelService.formatProps({ ...props }, ['label'], formatters);
        return MessageService.translate(label);
    }
    /**
     * Prettifies a value by returning label when the value is true.
     *
     * @param {object} properties - Properties to generate pretty value.
     * @param {string} [properties.label] - Label returned when value is true.
     * @param {boolean} [properties.value] - Value that dictates whether to return label.
     */
    static formatPrettyValue(props) {
        const { value } = props;
        return value ? Checkbox_1.getLabelText(props) : '';
    }
    shouldComponentUpdate(nextProps) {
        return shallowCompare(this.props, nextProps, [
            'className',
            'enabled',
            'focused',
            'information',
            'label',
            'name',
            'readOnly',
            'strikethrough',
            'uuid',
            'value',
        ]);
    }
    componentDidUpdate(prevProps) {
        const { focused } = this.props;
        if (!this.checkboxRef || !this.checkboxRef.current) {
            return;
        }
        if (prevProps.focused !== focused && focused) {
            this.checkboxRef.current.focus();
        }
    }
    getInputId() {
        const { identifier, name, uuid = Checkbox_1.defaultProps.uuid } = this.props;
        const baseValue = name || uuid;
        const identifierValue = identifier ? `-${identifier}` : '';
        return `check-${baseValue}${identifierValue}`;
    }
    renderInformation() {
        if (!isContentfulString(this.props.information)) {
            return;
        }
        return (React.createElement(Tooltip, { alignment: Alignment.TOP, multiline: true, placement: Placement.RIGHT, value: this.props.information },
            React.createElement("div", { className: "c-checkbox__label-icon-container" },
                React.createElement(InformationCircleIcon, { className: "c-checkbox__label-icon" }))));
    }
    render() {
        const { className, enabled = Checkbox_1.defaultProps.enabled, label, name, readOnly, strikethrough, value, } = this.props;
        const inputId = this.getInputId();
        const rootClasses = {
            'c-checkbox': true,
            'is-checked': value,
            'u-text-strikethrough': Boolean(strikethrough),
            [`${className}`]: Boolean(className),
            ...defaultClasses({ enabled, readOnly: Boolean(readOnly) }),
        };
        return (React.createElement("div", { className: classNames(rootClasses) },
            React.createElement("input", { checked: value, className: "c-checkbox__input", disabled: readOnly || !enabled, id: inputId, name: name, onBlur: this.onBlur, onChange: this.onChange, onFocus: this.onFocus, ref: this.checkboxRef, type: "checkbox" }),
            React.createElement("label", { className: "c-checkbox__label", htmlFor: inputId },
                React.createElement("div", { className: "c-checkbox__box", "x-contrast": "bg" },
                    React.createElement(CheckMark, { className: "c-checkbox__icon" })),
                label && React.createElement("span", null, Checkbox_1.getLabelText(this.props)),
                this.renderInformation())));
    }
};
Checkbox.propTypes = {
    className: string,
    enabled: bool,
    focused: bool,
    formatters: formatters,
    identifier: oneOfType([number, string]).notEnumerable(),
    information: string,
    label: expression,
    name: string,
    onBlur: eventListener,
    onChange: eventListener,
    onFocus: eventListener,
    readOnly: bool,
    strikethrough: bool,
    uuid: string,
    value: bool,
};
Checkbox.defaultProps = {
    enabled: true,
    label: EditorService.isEditor() ? 'Checkbox' : '',
    uuid: '',
    value: false,
};
Checkbox = Checkbox_1 = __decorate([
    AwesomeComponent({ api: CheckboxAPI, type: ComponentType.DATA_ENTRY })
], Checkbox);
export { Checkbox, };
//# sourceMappingURL=checkbox.js.map